import {
  addDays,
  eachDayOfInterval,
  endOfDay,
  isSameDay,
  startOfDay,
} from 'date-fns';
import { useHabits, useTasks } from 'features/tasks';
import { useMemo } from 'react';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { ScheduleEntryState } from 'shared/types/habit-schedule';
import { Task } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { formatDateKey } from 'shared/utils/format-date-key';
import { getActiveSchedule } from 'shared/utils/get-active-schedule';
import { getScheduleCompletedOnDate } from 'shared/utils/get-schedule-completed-on-date';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

export const useCompletedDateSections = () => {
  const today = useToday();
  // subtract 30 days from today to get the completedAt date
  const completedAt = useMemo(() => addDays(today, -30), [today]);
  const todos = useTasks({ completedAt, noScheduleOnly: true });
  const repeating = useTasks({ type: TaskType.Repeating });
  const habits = useHabits();

  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);

  const tasks = useMemo(() => {
    // combine todos, repeating tasks and habits
    const initialTasks = [...todos, ...repeating, ...habits];

    // filter tasks
    const filteredTasks = initialTasks.filter(
      ({ parentIds }) => !parentIds?.length,
    );

    // sort tasks
    return filteredTasks.sort((taskA, taskB) => {
      // Sort on completedAt dates (latest first)
      if (taskA.completedAt || taskB.completedAt) {
        return (
          (taskB.completedAt?.getTime() || 0) -
          (taskA.completedAt?.getTime() || 0)
        );
      }

      // Fall back to createdAt dates (latest first)
      return (
        (taskB.createdAt?.getTime() || 0) - (taskA.createdAt?.getTime() || 0)
      );
    });
  }, [todos, repeating, habits]);

  const sections = useMemo(() => {
    // create a map of dates between today and completedAt
    const dateMap = eachDayOfInterval({
      start: completedAt,
      end: today,
    }).reduce<Record<string, { date: Timestamp; tasks: Task[] }>>(
      (acc, date) => {
        acc[formatDateKey(date)] = { date: startOfDay(date), tasks: [] };
        return acc;
      },
      {},
    );

    // add the dates where tasks have completions on
    tasks.forEach((task) => {
      if (task.completedAt) {
        const dateKey = formatDateKey(task.completedAt);
        dateMap[dateKey]?.tasks.push(task);
        return;
      }

      if (!task.schedules) {
        return;
      }
      // if completed today, add to today
      const activeSchedule = getActiveSchedule(task.schedules);
      if (
        activeSchedule &&
        getScheduleCompletedOnDate(activeSchedule, today, {
          weekStartsOn,
          fullyCompleted: true,
          hasCompletionOnDate: true,
        })
      ) {
        const dateKey = formatDateKey(today);
        dateMap[dateKey]?.tasks.push(task);
        return;
      }

      // get all completed schedule entries
      const completions = task.schedules
        .map(({ entries }) =>
          entries.filter(
            ({ state, value }) =>
              state === ScheduleEntryState.Complete && !!value,
          ),
        )
        .flat(1)
        .sort(
          (entryA, entryB) => entryB.date.getTime() - entryA.date.getTime(),
        );
      // get the latest completion and second to last completion
      const [lastCompletion, secondToLastCompletion] = completions;

      // check if lastCompletion is today
      if (lastCompletion && isSameDay(lastCompletion.date, today)) {
        // if it was fully completed today, it was added before. So we add the secondToLastCompletion date.
        const dateKey = secondToLastCompletion
          ? formatDateKey(secondToLastCompletion.date)
          : '';
        dateMap[dateKey]?.tasks.push(task);
        return;
      }

      // if lastCompletion is not today, add the lastCompletion
      const dateKey = lastCompletion ? formatDateKey(lastCompletion.date) : '';
      dateMap[dateKey]?.tasks.push(task);
      return;
    });

    // create sections from the map
    return Object.entries(dateMap)
      .filter(([_, { tasks }]) => tasks.length)
      .map(([dateKey, { date, tasks }]) => ({
        id: dateKey,
        startDate: startOfDay(date),
        endDate: endOfDay(date),
        items: tasks,
      }))
      .sort((a, b) => b.startDate.getTime() - a.startDate.getTime());
  }, [completedAt, tasks, today, weekStartsOn]);

  return { sections, tasks };
};
